import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePlus, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import ListSortType from '../../../../utility/types/listSortType';
import SortableTableHeader from '../../../common/SortableTableHeader/SortableTableHeader';
import { selectRoomAlertDeviceList } from '../../../../redux/slices/deviceList/deviceListSlice';

const columnLabelMap = [
  { label: 'DeviceName', displayLabel: 'Device Name', sortType: ListSortType.DEVICE_NAME },
  { label: 'SensorName', displayLabel: 'Sensor Name', sortType: ListSortType.SENSOR_NAME },
  { label: 'ComponentName', displayLabel: 'Component Name', sortType: ListSortType.COMPONENT_NAME },
  { label: 'SensorType', displayLabel: 'Sensor Type', sortType: ListSortType.SENSOR_TYPE },
  { label: 'SensorChannel', displayLabel: 'Sensor Channel', sortType: ListSortType.NULLENUM },
  { label: 'Port', displayLabel: 'Port', sortType: ListSortType.NULLENUM },
  { label: 'LastReading', displayLabel: 'Last Reading', sortType: ListSortType.NULLENUM },
  { label: 'LastReceived', displayLabel: 'Last Received', sortType: ListSortType.LAST_RECEIVED },
  { label: 'DeviceType', displayLabel: 'Device Type', sortType: ListSortType.DEVICE_TYPE },
  { label: 'MACAddress', displayLabel: 'MAC Address', sortType: ListSortType.MAC_ADDRESS },
  { label: 'SerialNumber', displayLabel: 'Serial Number', sortType: ListSortType.SERIAL_NUMBER },
];

const ChannelPickerTable = ({
  channelList,
  columnVisibility,
  selectedChannelId,
  onAddChannel,
  sort,
  sortType,
  onSortChange,
}) => {
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const hasMaxDevice = !!deviceList.find((d) => d.type === 'RAMAX');
  const columns = hasMaxDevice
    ? columnLabelMap
    : columnLabelMap.filter((c) => c.label !== 'ComponentName');

  const normalizedSelectedId = selectedChannelId != null && selectedChannelId !== ''
    ? String(selectedChannelId)
    : null;

  return (
    <table className="table table-sm table-condensed table-striped my-1">
      <thead className="thead-light">
        <tr>
          <th aria-label="Select" />
          {columns.map((col) => {
            if (columnVisibility[col.label] !== true) return null;

            if (col.sortType === ListSortType.NULLENUM) {
              return (
                <th key={col.label} className="align-content-center">
                  <div>{col.displayLabel}</div>
                </th>
              );
            }

            return (
              <SortableTableHeader
                key={col.label}
                onDescClick={() => onSortChange('DES', col.sortType)}
                onAscClick={() => onSortChange('ASC', col.sortType)}
                label={col.displayLabel}
                isSelected={col.sortType === sortType && sort != null}
              />
            );
          })}
        </tr>
      </thead>
      <tbody>
        {channelList.map((channel, index) => {
          const isSelected = normalizedSelectedId != null
          && channel.channelId != null
          && String(channel.channelId) === normalizedSelectedId;
          const cellStyle = (label) => (columnVisibility[label] === false ? { display: 'none' } : {});
          return (
            <tr key={channel.channelKey || index} className={isSelected ? 'table-active' : ''}>
              <td style={{ paddingLeft: '0.75rem', paddingRight: '1rem' }}>
                <FontAwesomeIcon
                  icon={isSelected ? faCircleCheck : faSquarePlus}
                  onClick={() => onAddChannel(channel)}
                  style={{ cursor: 'pointer' }}
                />
              </td>
              {columnVisibility.DeviceName && (
              <td className="ellipsis" style={cellStyle('DeviceName')} title={channel.deviceName}>
                <span>{channel.deviceName}</span>
              </td>
              )}
              {columnVisibility.SensorName && (
              <td className="ellipsis" style={cellStyle('SensorName')} title={channel.sensorName}>
                <span>{channel.sensorName}</span>
              </td>
              )}
              {(columnVisibility.ComponentName && hasMaxDevice) && (
              <td className="ellipsis" style={cellStyle('ComponentName')} title={channel.componentName}>
                <span>{channel.componentName}</span>
              </td>
              )}
              {columnVisibility.SensorType && (
              <td className="ellipsis" style={cellStyle('SensorType')} title={channel.sensorType}>
                <span>{channel.sensorType}</span>
              </td>
              )}
              {columnVisibility.SensorChannel && (
              <td className="ellipsis" style={cellStyle('SensorChannel')} title={channel.sensorChannel}>
                <span>{channel.sensorChannel}</span>
              </td>
              )}
              {columnVisibility.Port && (
              <td className="ellipsis" style={cellStyle('Port')} title={channel.port}>
                <span>{channel.port}</span>
              </td>
              )}
              {columnVisibility.LastReading && (
              <td className="ellipsis" style={cellStyle('LastReading')} title={channel.lastReading}>
                <span>{channel.lastReading}</span>
              </td>
              )}
              {columnVisibility.LastReceived && (
              <td className="ellipsis" style={cellStyle('LastReceived')} title={channel.lastReceived}>
                <span>{channel.lastReceived}</span>
              </td>
              )}
              {columnVisibility.DeviceType && (
              <td className="ellipsis" style={cellStyle('DeviceType')} title={channel.deviceType}>
                <span>{channel.deviceType}</span>
              </td>
              )}
              {columnVisibility.MACAddress && (
              <td className="ellipsis" style={cellStyle('MACAddress')} title={channel.macAddress}>
                <span>{channel.macAddress}</span>
              </td>
              )}
              {columnVisibility.SerialNumber && (
              <td className="ellipsis" style={cellStyle('SerialNumber')} title={channel.serialNumber}>
                <span>{channel.serialNumber}</span>
              </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

ChannelPickerTable.propTypes = {
  channelList: PropTypes.arrayOf(PropTypes.shape({
    channelKey: PropTypes.string,
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  columnVisibility: PropTypes.shape({
    DeviceName: PropTypes.bool,
    SensorName: PropTypes.bool,
    ComponentName: PropTypes.bool,
    SensorType: PropTypes.bool,
    SensorChannel: PropTypes.bool,
    Port: PropTypes.bool,
    LastReading: PropTypes.bool,
    LastReceived: PropTypes.bool,
    DeviceType: PropTypes.bool,
    MACAddress: PropTypes.bool,
    SerialNumber: PropTypes.bool,
    Alerts: PropTypes.bool,
  }).isRequired,
  selectedChannelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAddChannel: PropTypes.func.isRequired,
  sort: PropTypes.string,
  sortType: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
};

ChannelPickerTable.defaultProps = {
  selectedChannelId: null,
  sort: null,
  sortType: null,
};

export default ChannelPickerTable;
